/**
 * One look for every user avatar in AVA (shared UserAvatar.vue and the Register Management
 * "Assigned" column). Showcase: src/assets/showcase/user-avatar-showcase.html, variant A (Tinted).
 *
 * - Identity colours only: 8 muted hues, never the status colours (red / amber / mint), so a
 *   user never reads as an error or a success.
 * - Soft tint background + dark same-hue initials (light theme); deep tint + light initials
 *   (dark theme). Every pair passes WCAG AA for the 11-12px initials (>= 6.3:1).
 * - The hue is a stable FNV-1a hash of the user id, so one person keeps one colour everywhere.
 */

export const AVATAR_TINTS_LIGHT = Object.freeze([
  { bg: '#dcefeb', fg: '#0b5a4f' }, // teal
  { bg: '#dde9f3', fg: '#1d4a70' }, // ocean
  { bg: '#e4e5f5', fg: '#393d86' }, // indigo
  { bg: '#eee2ef', fg: '#652d69' }, // plum
  { bg: '#f5e5da', fg: '#83401d' }, // copper
  { bg: '#e8ecd9', fg: '#48541a' }, // olive
  { bg: '#e5e8ed', fg: '#394352' }, // slate
  { bg: '#f1eada', fg: '#654f16' }, // sand
]);

export const AVATAR_TINTS_DARK = Object.freeze([
  { bg: '#17332f', fg: '#8fd3c5' },
  { bg: '#172b3b', fg: '#9cc4e6' },
  { bg: '#23244a', fg: '#b3b6f0' },
  { bg: '#33223a', fg: '#dcb1e0' },
  { bg: '#3a261b', fg: '#f0b995' },
  { bg: '#2a2f18', fg: '#c9d690' },
  { bg: '#262b33', fg: '#bcc6d4' },
  { bg: '#352d17', fg: '#e3cc8f' },
]);

/** Neutral slate: the "+N" overflow circle and the "no user" placeholder. */
export const AVATAR_NEUTRAL_LIGHT = Object.freeze({ bg: '#e5e8ed', fg: '#394352' });
export const AVATAR_NEUTRAL_DARK = Object.freeze({ bg: '#262b33', fg: '#bcc6d4' });

/** FNV-1a 32-bit. Spreads similar ids (GUIDs sharing prefixes) across the hues. */
export function hashAvatarSeed(seed) {
  let hash = 2166136261;
  const text = String(seed == null ? '' : seed);
  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

export function getAvatarColors(seed, isDark = false) {
  const tints = isDark ? AVATAR_TINTS_DARK : AVATAR_TINTS_LIGHT;
  return tints[hashAvatarSeed(seed) % tints.length];
}

export function getNeutralAvatarColors(isDark = false) {
  return isDark ? AVATAR_NEUTRAL_DARK : AVATAR_NEUTRAL_LIGHT;
}

/** Up to two initials from a display name ("Khalid Dev" -> "KD", "khalid" -> "K"); "?" when empty. */
export function getInitials(name) {
  const parts = String(name == null ? '' : name).trim().split(/\s+/).filter(Boolean);
  const letters = parts.slice(0, 2).map((part) => part.charAt(0).toUpperCase()).join('');
  return letters || '?';
}

/**
 * Which avatars a stack shows. With exactly one user over the limit, show all of them instead
 * of trading an avatar for a "+1" circle that takes the same space.
 */
export function splitAvatarStack(users, max = 3) {
  const list = Array.isArray(users) ? users : [];
  const limit = Math.max(1, Number(max) || 1);
  if (list.length <= limit + 1) return { shown: list, overflow: 0 };
  return { shown: list.slice(0, limit), overflow: list.length - limit };
}
